import {flowchartInstanceApi} from "@/container";
import {FlowchartInstancePath} from "@/core/interfaces/flowchart/flowchart";
import Customer from "@/customer/interfaces/Customer";
import Beneficiary from "@/customer/interfaces/Beneficiary";
import {useToastStore} from "@/stores/toast.store";

/**
 * Keep a single flowchart instance per passage through a flowchart: created when the
 * first terminal node opens, then updated when the pharmacist reaches another terminal
 * node or prints a document.
 *
 * Calls are chained, so an update never overtakes the creation of the instance.
 */
const useFlowchartInstance = () => {
    const {showError} = useToastStore();

    let instanceId: number | undefined;
    let savedPath: FlowchartInstancePath | undefined;
    let savedConsumerKey: string | undefined;
    let queue: Promise<void> = Promise.resolve();

    /**
     * Run a task once the previous ones are settled.
     *
     * @param task The task to run.
     */
    const enqueue = (task: () => Promise<void> | void): Promise<void> => {
        queue = queue
            .then(task)
            .catch(() => showError({content: 'Le parcours n\'a pas pu être enregistré dans l\'historique du patient'}));

        return queue;
    };

    /**
     * Create the instance of the passage, or update it once created. A path without
     * edges has no verdict to show in the history, so it is never saved.
     */
    const persist = async (flowchartId: number, consumer: Customer | Beneficiary, path: FlowchartInstancePath) => {
        if (!path.length) {
            return;
        }

        const instance = instanceId && savedConsumerKey === consumer.consumer_key
            ? await flowchartInstanceApi().update(instanceId, path)
            : await flowchartInstanceApi().store(flowchartId, consumer, path);

        instanceId = instance.id;
        savedPath = path;
        savedConsumerKey = consumer.consumer_key;
    };

    /**
     * Whether both paths go through the same edges, whatever their options.
     */
    const sameEdges = (path: FlowchartInstancePath, other?: FlowchartInstancePath): boolean => {
        return JSON.stringify(path.map(item => item.slug)) === JSON.stringify(other?.map(item => item.slug));
    };

    /**
     * Record the terminal node the pharmacist opens. Reopening the node of the saved
     * path is ignored, so the options and products saved on a print are kept.
     *
     * @param flowchartId The flowchart id.
     * @param consumer The patient the flowchart is run for.
     * @param path The path to the terminal node, options still empty.
     */
    const open = (flowchartId: number, consumer: Customer | Beneficiary, path: FlowchartInstancePath) => enqueue(() => {
        if (savedConsumerKey === consumer.consumer_key && sameEdges(path, savedPath)) {
            return;
        }

        return persist(flowchartId, consumer, path);
    });

    /**
     * Save the complete path, once the pharmacist acts on the terminal node.
     *
     * @param flowchartId The flowchart id.
     * @param consumer The patient the flowchart is run for.
     * @param path The path with the filled options and the reserved terminal node options.
     */
    const save = (flowchartId: number, consumer: Customer | Beneficiary, path: FlowchartInstancePath) => enqueue(
        () => persist(flowchartId, consumer, path)
    );

    /**
     * Start a new passage: the next terminal node creates a new instance.
     */
    const reset = () => enqueue(() => {
        instanceId = undefined;
        savedPath = undefined;
        savedConsumerKey = undefined;
    });

    return {open, save, reset};
};

export type FlowchartInstanceSession = ReturnType<typeof useFlowchartInstance>;

export default useFlowchartInstance;
